import { useState, useRef, useEffect, useCallback } from 'react';
import { inputStyles } from '@/styles/input';
import { cn } from '@/utils/cn';
import { Loader } from './Loader';
import { useTranslate } from '@/hooks/useTranslate';

export interface AutoCompleteOption {
  label: string;
  value: string;
}

interface AutoCompleteProps {
  options: AutoCompleteOption[];
  onSelect: (option: AutoCompleteOption) => void;
  value?: string;
  placeholder?: string;
  className?: string;
  error?: string;
  disabled?: boolean;
  onInputChange: (value: string) => void;
  isLoading?: boolean;
  disableFiltering?: boolean;
  forceSelection?: boolean;
}

export const AutoComplete = ({
  options,
  onSelect,
  value = '',
  placeholder = 'Search...',
  className,
  error,
  disabled = false,
  onInputChange,
  isLoading = false,
  disableFiltering = false,
  forceSelection = false,
}: AutoCompleteProps) => {
  const { t } = useTranslate();
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const lastSelectedRef = useRef<AutoCompleteOption | null>(null);

  const filteredOptions = disableFiltering
    ? options
    : options.filter((option) => option.label.toLowerCase().includes(inputValue.toLowerCase()));

  // When forceSelection is on, any close path that isn't a click on an
  // option must revert the input to the last selected option (or empty).
  // Driving this from each close handler keeps the behavior off the
  // typing path entirely.
  const revertToLastSelection = useCallback(() => {
    if (!forceSelection) return;
    const desired = lastSelectedRef.current?.label ?? '';
    if (inputValue !== desired) {
      setInputValue(desired);
      onInputChange(desired);
    }
  }, [forceSelection, inputValue, onInputChange]);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        revertToLastSelection();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [revertToLastSelection]);

  useEffect(() => {
    if (isOpen && listRef.current && highlightedIndex >= 0) {
      const highlightedElement = listRef.current.children[highlightedIndex] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [highlightedIndex, isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setIsOpen(true);
    setHighlightedIndex(0);
    onInputChange(newValue);
  };

  const handleSelect = useCallback(
    (option: AutoCompleteOption) => {
      lastSelectedRef.current = option;
      setInputValue(option.label);
      onSelect(option);
      setIsOpen(false);
      inputRef.current?.blur();
    },
    [onSelect]
  );

  const handleBlur = () => {
    if (forceSelection) {
      setIsOpen(false);
      revertToLastSelection();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      setIsOpen(true);
      return;
    }

    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev < filteredOptions.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        revertToLastSelection();
        inputRef.current?.blur();
        break;
      default:
        break;
    }
  };

  const handleFocus = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };

  return (
    <div className="flex flex-col gap-1 w-full">
      <div className="relative w-full" ref={containerRef}>
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          className={cn(inputStyles, className, error && 'border-danger', disabled && 'opacity-50 cursor-not-allowed')}
        />

        <div
          ref={listRef}
          className={cn(
            'absolute top-full left-0 right-0 mt-1 bg-white border-1 border-base-border rounded-md z-50',
            'max-h-50 overflow-y-auto overflow-x-hidden',
            'transition-all duration-200 ease-in-out origin-top shadow-lg',
            isOpen && (filteredOptions.length > 0 || isLoading)
              ? 'opacity-100 scale-y-100 translate-y-0'
              : 'opacity-0 scale-y-0 -translate-y-2 pointer-events-none'
          )}
        >
          {isLoading ? (
            <div className="p-6 flex items-center justify-center">
              <Loader size="sm" color="blue" />
            </div>
          ) : filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <div
                key={option.value}
                // Session Replay: suggestion labels can be real street
                // addresses (PII). `mp-mask` masks their text in replays.
                data-mp-mask
                className={cn(
                  'mp-mask p-3 cursor-pointer transition-colors duration-150 text-sm',
                  index === highlightedIndex
                    ? 'bg-base-muted text-card-foreground'
                    : 'bg-white text-card-foreground hover:bg-base-muted'
                )}
                // Keep focus on the input so the input's blur (and revert
                // logic under forceSelection) doesn't fire before onClick.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(option)}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                {option.label}
              </div>
            ))
          ) : (
            <div className="p-3 text-sm text-muted-foreground text-center">{t('noResultsFound')}</div>
          )}
        </div>
      </div>
      {error && <div className="text-xs text-danger">{error}</div>}
    </div>
  );
};
